import { PublicApiClient } from '../api/client';
import { mountHeaderAuth } from '../components/headerAuth';
import { createPublicObservability, routeObservabilityTags } from '../observability/metrics';
import { createPublicTelemetry } from '../telemetry/events';
import { reportRuntimeError } from '../telemetry/errorReporter';
import { observeRuntimePerformance } from '../telemetry/webVitals';
import { findRuntimeRoot, readJsonScript } from './dom';
import { createPublicLanguageContext } from './i18n';
import { RuntimeLifecycle } from './lifecycle';
import { createPublicLogger } from './logger';
import { PUBLIC_PAGE_REGISTRY } from './page-registry';
import { createPublicRouter } from './public-router';
import { matchPublicRoute } from './route-registry';
import { readRuntimeConfig, resolveRuntimeApiBase } from './runtime-config';
import type { PublicPageController, PublicPageDefinition, PublicRuntimeConfig } from './types';

export type PublicRuntime = {
  boot(): Promise<void>;
  destroy(): void;
};

export function createPublicRuntime(doc: Document = document): PublicRuntime {
  const startedAt = performance.now();
  const config = readRuntimeConfig(doc);
  const route = matchPublicRoute(window.location.pathname, window.location.hash);
  const language = createPublicLanguageContext({
    routeLang: route.lang,
    runtimeDefaultLang: config.defaultLang,
  });
  const observability = createPublicObservability();
  const telemetry = createPublicTelemetry({ observability });
  const lifecycle = new RuntimeLifecycle();
  const logger = createPublicLogger();
  let activeController: PublicPageController | null = null;
  let headerAuthController: PublicPageController | null = null;
  const router = createPublicRouter({
    doc,
    win: window,
    telemetry,
    enabled: config.featureFlags?.['public.router.enhancement.enabled'] === true,
    hasTransitionHandler: false,
  });

  const api = new PublicApiClient({
    baseUrl: resolveRuntimeApiBase(config),
    signal: lifecycle.signal,
    observability,
  });

  return {
    async boot() {
      router.start();
      headerAuthController = mountHeaderAuth({
        doc,
        apiBase: resolveRuntimeApiBase(config),
        signal: lifecycle.signal,
      });
      const definition = resolveDefinition(doc, route.id);
      if (!definition) {
        telemetry.emit({
          name: 'public_runtime_no_page',
          pageId: route.id,
          route: route.canonicalPath,
          ok: true,
        });
        return;
      }

      const root = findRuntimeRoot(definition.selector, doc);
      if (!root) {
        telemetry.emit({
          name: 'public_runtime_no_root',
          pageId: definition.id,
          route: route.canonicalPath,
          ok: false,
        });
        return;
      }

      try {
        const pageMountStartedAt = performance.now();
        telemetry.emit({
          name: 'public_page_mount_started',
          pageId: definition.id,
          route: route.canonicalPath,
          ok: true,
          context: routeObservabilityTags(route),
        });
        const pageConfig = readPageConfig(definition, config, doc);
        const module = await definition.load();
        const controller = await module.mount({
          root,
          route,
          lang: language.uiLanguage,
          language,
          config: pageConfig,
          api,
          telemetry,
          signal: lifecycle.signal,
        });
        activeController = controller || null;
        telemetry.emit({
          name: 'public_page_mount_succeeded',
          pageId: definition.id,
          route: route.canonicalPath,
          ok: true,
          durationMs: Math.round(performance.now() - pageMountStartedAt),
          context: routeObservabilityTags(route),
        });
        observeRuntimePerformance(telemetry, definition.id, startedAt);
      } catch (error) {
        telemetry.emit({
          name: 'public_page_mount_failed',
          pageId: definition.id,
          route: route.canonicalPath,
          ok: false,
          context: routeObservabilityTags(route),
        });
        reportRuntimeError(telemetry, definition.id, error);
        logger.error('Page mount failed', { pageId: definition.id, error });
      }
    },
    destroy() {
      router.destroy();
      activeController?.destroy();
      headerAuthController?.destroy();
      lifecycle.destroy();
    },
  };
}

function resolveDefinition(doc: Document, routeId: string): PublicPageDefinition | null {
  const routeMatch = PUBLIC_PAGE_REGISTRY.find((definition) => definition.id === routeId);
  if (routeMatch) return routeMatch;
  return (
    PUBLIC_PAGE_REGISTRY.find((definition) => Boolean(doc.querySelector(definition.selector))) ||
    null
  );
}

function readPageConfig(
  definition: PublicPageDefinition,
  config: PublicRuntimeConfig,
  doc: Document,
): unknown {
  const pageConfig = definition.configId ? readJsonScript(definition.configId, doc) : null;
  return {
    runtime: config,
    page: pageConfig || {},
  };
}
